import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { AppLayout } from "../components/layout";
import { Card, CardContent } from "@/components/ui";
import { AlertCircle, HelpCircle } from "lucide-react";
import { getHelpCenters, createHelpCenter, deleteHelpCenter } from "../services/api";
import type { HelpCenter } from "../types";
import { EmptyState } from "@/components/ui/empty-state";
import { useUIStore } from "../store/uiStore";
import { useBillingInfo, useIsProCloudPlan } from "../store/userStore";
import UpsellModal from "../components/modals/UpsellModal";
import {
  HelpCentersTable,
  CreateHelpCenterModal,
  EditHelpCenterModal,
  DeleteHelpCenterModal,
  HelpCentersPageHeader,
  HelpCentersLoadingState,
  HelpCenterSettingsDrawer,
} from "../components/features/help-center";

const HelpCentersPage: React.FC = () => {
  const [helpCenters, setHelpCenters] = useState<HelpCenter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [helpCenterToDelete, setHelpCenterToDelete] = useState<number | null>(null);
  const [helpCenterToDeleteTitle, setHelpCenterToDeleteTitle] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createFormError, setCreateFormError] = useState<string | null>(null);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [helpCenterToEdit, setHelpCenterToEdit] = useState<number | null>(null);
  const { openModal, closeModal, modals } = useUIStore();
  const isProCloudPlan = useIsProCloudPlan();
  const billingInfo = useBillingInfo();
  
  const breadcrumbs = [
    { title: "Dashboard", href: "dashboard" },
    { title: "Help Centers", isCurrentPage: true },
  ];

  useEffect(() => {
    fetchHelpCenters();
  }, []);

  const fetchHelpCenters = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getHelpCenters();
      if (response.ok && response.helpCenters) {
        const sorted = response.helpCenters.sort(
          (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        );
        setHelpCenters(sorted);
      } else {
        const errorMessage = response.errors && response.errors.length > 0 
          ? response.errors[0] 
          : response.error || "Failed to fetch help centers";
        setError(errorMessage);
      }
    } catch (err) {
      console.error("Error fetching help centers:", err);
      setError("Failed to fetch help centers");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHelpCenter = (helpCenterId: number) => {
    const helpCenter = helpCenters.find((hc) => hc.id === helpCenterId);
    setHelpCenterToDelete(helpCenterId);
    setHelpCenterToDeleteTitle(helpCenter?.title || null);
    openModal('delete');
  };

  const handleConfirmDelete = async () => {
    if (!helpCenterToDelete) return;
    
    closeModal('delete');
    setIsDeleting(true);
    
    try {
      const result = await deleteHelpCenter(helpCenterToDelete.toString());
      if (result.ok) {
        toast.success("Help center deleted successfully", {
          position: "top-center",
          duration: 3000,
        });
        
        await fetchHelpCenters();
        setHelpCenterToDelete(null);
        setHelpCenterToDeleteTitle(null);
      } else {
        const errorMessage = result.errors && result.errors.length > 0 
          ? result.errors[0] 
          : result.error || "Failed to delete help center";
        toast.error(errorMessage, {
          position: "top-center",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Error deleting help center:', error);
      toast.error("Failed to delete help center", {
        position: "top-center",
        duration: 3000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    closeModal('delete');
    setHelpCenterToDelete(null);
    setHelpCenterToDeleteTitle(null);
  };

  const handleCreateHelpCenter = async (form: { title: string; description: string; slug: string }) => {
    // Check limits before attempting to create
    if (!isProCloudPlan) {
      const helpCentersLimit = billingInfo?.helpCentersLimit ?? 1;
      const helpCenterCreated = billingInfo?.helpCenterCreated ?? 0;
      if (helpCenterCreated >= helpCentersLimit) {
        setCreateFormError(`Free plan allows only ${helpCentersLimit} help center. Upgrade to create more help centers.`);
        openModal('upsell');
        return;
      }
    }

    // Clear previous errors
    setCreateFormError(null);
    setIsCreating(true);
    
    try {
      const result = await createHelpCenter({
        title: form.title,
        description: form.description || undefined,
        slug: form.slug || undefined,
      });

      if (result.ok && result.helpCenter) {
        toast.success("Help center created successfully", {
          position: "top-center",
          duration: 3000,
        });
        
        setShowCreateModal(false);
        setCreateFormError(null);
        await fetchHelpCenters();
      } else {
        // Display server-side validation error in modal
        // Handle both error (string) and errors (array) formats
        const errorMessage = result.errors && result.errors.length > 0 
          ? result.errors[0] 
          : result.error || "Failed to create help center";
        setCreateFormError(errorMessage);
        // If error mentions limit or upgrade, show upsell modal
        if (errorMessage.toLowerCase().includes('limit') || errorMessage.toLowerCase().includes('upgrade')) {
          openModal('upsell');
        }
      }
    } catch (error) {
      console.error('Error creating help center:', error);
      setCreateFormError("Failed to create help center. Please try again.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditHelpCenter = (helpCenterId: number) => {
    setHelpCenterToEdit(helpCenterId);
    setShowEditDrawer(true);
  };

  const handleEditSuccess = () => {
    fetchHelpCenters();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return <HelpCentersLoadingState breadcrumbs={breadcrumbs} />;
  }

  return (
    <AppLayout showSidebar={true} breadcrumbs={breadcrumbs}>
      <div className="p-4">
        <HelpCentersPageHeader onCreateClick={() => setShowCreateModal(true)} />

        {/* Error State */}
        {error && (
          <Card className="border-destructive mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-4 w-4" />
                <span>{error}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Help Centers Table */}
        {helpCenters.length === 0 && !error ? (
          <EmptyState
            title="No help centers yet"
            description="Create your first help center to organize articles and guides"
            icons={[HelpCircle, HelpCircle, HelpCircle]}
            className="mx-auto"
          />
        ) : (
          <HelpCentersTable
            helpCenters={helpCenters}
            onEdit={handleEditHelpCenter}
            onDelete={handleDeleteHelpCenter}
            formatDate={formatDate}
          />
        )}
      </div>

      <CreateHelpCenterModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setCreateFormError(null);
        }}
        onSubmit={handleCreateHelpCenter}
        isCreating={isCreating}
        error={createFormError}
      />

      <HelpCenterSettingsDrawer
        isOpen={showEditDrawer}
        onClose={() => {
          setShowEditDrawer(false);
          setHelpCenterToEdit(null);
        }}
        helpCenterId={helpCenterToEdit}
        onSuccess={handleEditSuccess}
      />

      <DeleteHelpCenterModal
        isOpen={modals.delete}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
        helpCenterTitle={helpCenterToDeleteTitle || undefined}
      />

      <UpsellModal
        isOpen={modals.upsell}
        onClose={() => closeModal("upsell")}
      />
    </AppLayout>
  );
};

export default HelpCentersPage;

